import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { BookOpen } from 'lucide-react';

const LibraryPage = () => {
  const { t } = useLanguage();

  const books = [
    { key: 'book1', available: true, hasCover: true },
    { key: 'book2', available: false, hasCover: false },
    { key: 'book3', available: false, hasCover: false },
    { key: 'book4', available: false, hasCover: false },
    { key: 'book5', available: false, hasCover: false },
    { key: 'book6', available: false, hasCover: false },
    { key: 'book7', available: false, hasCover: false },
    { key: 'book8', available: false, hasCover: false },
  ];

  const months = [
    'JANVIER', 'FÉVRIER', 'MARS', 'AVRIL', 'MAI', 'JUIN',
    'JUILLET', 'AOÛT', 'SEPTEMBRE', 'OCTOBRE', 'NOVEMBRE', 'DÉCEMBRE'
  ];

  return (
    <div 
      data-testid="library-page"
      className="min-h-screen pt-24"
      style={{ backgroundColor: '#050A14' }}
    >
      {/* Hero Section */}
      <section className="py-20 md:py-28 text-center">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex justify-center mb-8">
            <div 
              className="w-16 h-16 rounded-full flex items-center justify-center"
              style={{ border: '1px solid rgba(156, 163, 175, 0.15)' }}
            >
              <BookOpen className="w-7 h-7" style={{ color: '#9CA3AF' }} strokeWidth={1} />
            </div>
          </div>
          <h1 
            data-testid="library-title"
            className="font-heading text-5xl md:text-6xl lg:text-7xl font-light mb-6"
            style={{ color: '#D1D5DB' }}
          >
            {t('library.title')}
          </h1>
          <p 
            className="font-heading text-lg md:text-xl italic"
            style={{ color: '#9CA3AF' }}
          >
            {t('library.subtitle')}
          </p>
          <div className="divider-silver w-20 mx-auto mt-12" />
        </div>
      </section>

      {/* Series Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-6">
          {/* Series Header */}
          <div className="text-center mb-16">
            <h2 
              className="font-heading text-3xl md:text-4xl font-light mb-4"
              style={{ color: '#D1D5DB' }}
            >
              {t('library.seriesTitle')}
            </h2>
            <p 
              className="font-body text-base italic"
              style={{ color: '#6B7280' }}
            >
              {t('library.seriesDescription')}
            </p>
          </div>

          {/* Books Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-8">
            {books.map((book, index) => (
              <div 
                key={book.key}
                className={`group ${!book.available ? 'opacity-40' : ''}`}
                data-testid={`book-${index + 1}`}
              >
                <div 
                  className={`aspect-[2/3] rounded-sm overflow-hidden mb-4 transition-all duration-500 ${
                    book.available ? 'group-hover:shadow-lg' : ''
                  }`}
                  style={{ 
                    border: '1px solid rgba(156, 163, 175, 0.1)',
                    backgroundColor: '#070D18'
                  }}
                >
                  {book.hasCover ? (
                    <img
                      src="https://customer-assets.emergentagent.com/job_metalove-preview/artifacts/anagkfd3_RoseECaldwell-ebook-modif%20%281%29.png"
                      alt={t(`library.${book.key}.title`)}
                      className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity duration-500"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <div className="text-center">
                        <p 
                          className="font-heading text-4xl md:text-5xl mb-2"
                          style={{ color: 'rgba(156, 163, 175, 0.1)' }}
                        >
                          {index + 1}
                        </p>
                        <p 
                          className="font-body text-xs tracking-[0.15em] uppercase"
                          style={{ color: '#4B5563' }}
                        >
                          {t('library.comingSoon')}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
                
                <p 
                  className="font-body text-xs tracking-[0.15em] uppercase mb-1"
                  style={{ color: '#6B7280' }}
                >
                  {t(`library.${book.key}.volume`)}
                </p>
                <h3 
                  className="font-heading text-lg md:text-xl"
                  style={{ color: book.available ? '#D1D5DB' : '#6B7280' }}
                >
                  {t(`library.${book.key}.title`)}
                </h3>
                
                {book.available && (
                  <>
                    <p 
                      className="font-body text-sm mt-2 leading-relaxed hidden md:block"
                      style={{ color: '#6B7280' }}
                    >
                      {t(`library.${book.key}.description`)}
                    </p>
                    <p
                      className="mt-3 text-xs tracking-[0.15em] uppercase"
                      style={{ color: '#4B5563' }}
                    >
                      Bientôt disponible
                    </p>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Second Series Section */}
      <section className="py-16 md:py-24" style={{ borderTop: '1px solid rgba(156, 163, 175, 0.06)' }}>
        <div className="max-w-6xl mx-auto px-6">
          {/* Series Header */}
          <div className="text-center mb-16">
            <p 
              className="font-body text-xs tracking-[0.3em] uppercase mb-6"
              style={{ color: '#4B5563' }}
            >
              {t('library.series2.tag')}
            </p>
            <h2 
              className="font-heading text-3xl md:text-4xl font-light mb-4"
              style={{ color: '#D1D5DB' }}
            >
              {t('library.series2.title')}
            </h2>
            <p 
              className="font-body text-base italic"
              style={{ color: '#6B7280' }}
            >
              {t('library.series2.description')}
            </p>
          </div>

          {/* Months Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-8">
            {months.map((month, index) => (
              <div 
                key={month}
                className="group opacity-40"
                data-testid={`series2-book-${index + 1}`}
              >
                <div 
                  className="aspect-[2/3] rounded-sm overflow-hidden mb-4"
                  style={{ 
                    border: '1px solid rgba(156, 163, 175, 0.1)',
                    backgroundColor: '#070D18'
                  }}
                >
                  <div className="w-full h-full flex items-center justify-center">
                    <div className="text-center">
                      <p 
                        className="font-heading text-3xl md:text-4xl mb-2"
                        style={{ color: 'rgba(156, 163, 175, 0.12)' }}
                      >
                        {index + 1}
                      </p>
                      <p 
                        className="font-body text-xs tracking-[0.15em] uppercase"
                        style={{ color: '#4B5563' }}
                      >
                        {t('library.comingSoon')}
                      </p>
                    </div>
                  </div>
                </div>
                <p 
                  className="font-body text-xs tracking-[0.15em] uppercase mb-1"
                  style={{ color: '#4B5563' }}
                >
                  {t('library.series2.volumeLabel')} {index + 1}
                </p>
                <h3 
                  className="font-heading text-lg md:text-xl"
                  style={{ color: '#6B7280' }}
                >
                  {month}
                </h3>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default LibraryPage;
